import {Component, OnInit} from "@angular/core";
import {
  AgentService, GetWorkstationAgentVersionsResponse,
  WorkstationAgentAvailableVersion,
  WorkstationAgentDeployedVersion
} from "../../../../services/back/agent.service";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {TenantOverview} from "../../../../services/tenant.service";
import {mergeMap, Observable, Subscription} from "rxjs";
import {DecimalPipe, NgIf} from "@angular/common";
import {DashboardPageLoaderComponent} from "../../../global/title-content-loader/dashboard-page-loader.component";
import {tap} from "rxjs/operators";
import {RefreshService} from "../refresh.service";
import {TranslateModule} from "@ngx-translate/core";

@Component({
  selector: 'app-agents-versions',
  templateUrl: './agents-versions.component.html',
  imports: [
    NgIf,
    DecimalPipe,
    DashboardPageLoaderComponent,
    TranslateModule
  ],
  styleUrls: ['./agents-versions.component.scss']
})
export class AgentsVersionsComponent implements OnInit {
  constructor(
    private agentService: AgentService,
    private refreshService: RefreshService,
    private tenantService: CurrentTenantService) { }

  private subscription: Subscription = new Subscription();
  private deployedVersions: WorkstationAgentDeployedVersion[] = [];
  private availableVersions: WorkstationAgentAvailableVersion[] = [];
  protected mainChartVersions: ChartVersionItem[] = [];
  protected barChartVersions: ChartVersionItem[] = [];
  protected _loading= true;

  ngOnInit(): void {
    this.subscription.add(this.tenantService.getCurrentTenantChanges().pipe(
      mergeMap((tenant: TenantOverview) => this.agentService.getWorkstationAgentVersions(tenant.configuration.id)),
      tap(() => this._loading = false),
    ).subscribe({
      next: versions => this.buildChartVersions(versions),
      error: () => {
        this._loading = false;
      }
    }));

    this.subscription.add(this.refreshService.onRefresh.pipe(mergeMap(() => this.onRefresh())).subscribe({}));
  }

  onRefresh(): Observable<any> {
    return this.tenantService.getCurrentTenantId()
      .pipe(mergeMap(tenantId => this.agentService.getWorkstationAgentVersions(tenantId)))
      .pipe(tap(versions => this.buildChartVersions(versions)));
  }

  buildChartVersions(versions: GetWorkstationAgentVersionsResponse): void {
    this.mainChartVersions = [];

    this.deployedVersions = versions.deployedVersion.sort((a, b) => b.count - a.count);
    this.availableVersions = versions.availableVersion;

    if ( this.deployedVersions.findIndex(item => item.version === this.availableVersions[0].version) === -1 ) {
      this.barChartVersions = [
        { version: this.availableVersions[0].version, width: 0, color: '#3DC0FF' }
      ]
    } else {
      this.barChartVersions = []
    }

    const totalAgentDeployed = this.deployedVersions.map(item => item.count).reduce((a, b) => a + b, 0);

    this.deployedVersions.forEach((version, index) => {
      if (index === 0) {
        this.mainChartVersions.push({ version: version.version, width: (version.count/totalAgentDeployed) * 100, color: '#3DC0FF' })
        this.barChartVersions.push({ version: version.version, width: (version.count/totalAgentDeployed) * 100, color: '#3DC0FF', count: version.count});
      } else {
        this.mainChartVersions.push({ version: version.version, width: (version.count/totalAgentDeployed) * 100, color: this.darkenColor('#3DC0FF', (1 / this.deployedVersions.length) * index) })
        this.barChartVersions.push({ version: version.version, width: (version.count/totalAgentDeployed) * 100, color: this.darkenColor('#3DC0FF', (1 / this.deployedVersions.length) * index), count: version.count });
      }
    });

    this.barChartVersions.sort((a, b) => b.version.localeCompare(a.version));
  }

  isLatest(version: string): boolean {
    return this.availableVersions[0].version === version;
  }

  darkenColor(hex: string, percent: number): string {
    // Validate hex format
    if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) {
      throw new Error('Invalid HEX color format.');
    }

    // Convert hex to RGB
    let r: number = parseInt(hex.slice(1, 3), 16);
    let g: number = parseInt(hex.slice(3, 5), 16);
    let b: number = parseInt(hex.slice(5, 7), 16);

    // Convert RGB to HSL
    let rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h: number, s: number, l: number = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case rNorm:
          h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
          break;
        case gNorm:
          h = (bNorm - rNorm) / d + 2;
          break;
        default: // case bNorm
          h = (rNorm - gNorm) / d + 4;
          break;
      }
      h /= 6;
    }

    // Darken the color by reducing the lightness
    l = Math.max(0, Math.min(1, l * (1 - percent)));

    // Convert back to RGB
    const hueToRgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    const rFinal = hueToRgb(p, q, h + 1 / 3);
    const gFinal = hueToRgb(p, q, h);
    const bFinal = hueToRgb(p, q, h - 1 / 3);

    // Convert RGB values to HEX
    const toHex = (x: number): string => {
      return Math.round(x * 255).toString(16).padStart(2, '0');
    };

    return `#${toHex(rFinal)}${toHex(gFinal)}${toHex(bFinal)}`;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}

interface ChartVersionItem {
  version: string;
  width: number;
  color: string;
  count?: number;
}